<template>
    <div class="tab-wrapper">
        <nav class="tab-nav">
            <button
                v-for="tab in tabs"
                :key="tab.id"
                :class="['tab-item', { 'tab-item--active': activeTabId === tab.id }]"
                @click="setActiveTab(tab)"
                type="button"
            >
                <i v-if="tab.icon" :class="['tab-icon', tab.icon]"></i>
                {{ tab.title }}
                <span v-if="tab.count !== undefined" class="tab-badge">{{ tab.count }}</span>
            </button>
        </nav>
        <div class="tab-indicator-bar">
            <div class="tab-indicator" :style="indicatorStyle" />
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';

const emit = defineEmits(['setActiveTabId']);

const tabs = [
    {
        id: 'new_appointment',
        title: 'New Appointment',
        icon: 'fas fa-calendar-plus',
    },
    {
        id: 'ongoing_treatment',
        title: 'Ongoing Treatment',
        icon: 'fas fa-tooth',
    }
];

const DEFAULT_TAB = tabs[0].id;
const activeTabId = ref(DEFAULT_TAB);

const indicatorStyle = computed(() => {
    const index = tabs.findIndex(t => t.id === activeTabId.value);
    const pct   = 100 / tabs.length;
    return {
        width:     `${pct}%`,
        transform: `translateX(${index * 100}%)`,
    };
});

const setActiveTab = (tab) => {
    if (activeTabId.value === tab.id) return;
    activeTabId.value = tab.id;
    emit('setActiveTabId', tab.id);
};
</script>

<style scoped>

.tab-nav {
    display: flex;
    align-items: stretch;
}
.tab-item {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 0.55rem 0.75rem;
    border: none;
    background: transparent;
    color: #6b7280;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.18s ease, color 0.18s ease;
    white-space: nowrap;
    position: relative;
}

.tab-item:hover:not(.tab-item--active) {
    background: #eef0f5;
    color: #374151;
}

.tab-item--active {
    background: #f1f0ff;
    color: #4f46e5;
    font-weight: 600;
}

.tab-icon {
    opacity: 0.85;
}

.tab-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 999px;
    background: #e0e7ff;
    color: #4f46e5;
    font-weight: 700;
    line-height: 1;
}

.tab-item--active .tab-badge {
    background: #4f46e5;
    color: #ffffff;
}

/* ── Sliding indicator ── */
.tab-indicator-bar {
    position: relative;
    height: 2px;
    background: #e9ecef;
    margin-top: -1px;
}

.tab-indicator {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    background: #4f46e5;
    border-radius: 2px;
    transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1),
                width    0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

/* ── Tab content fade+slide transition ── */
.tab-fade-enter-active,
.tab-fade-leave-active {
    transition: opacity 0.18s ease, transform 0.18s ease;
}

.tab-fade-enter-from {
    opacity: 0;
    transform: translateY(6px);
}

.tab-fade-leave-to {
    opacity: 0;
    transform: translateY(-4px);
}
</style>